import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import { setFormType } from "../../auth/authSlice";
import PrimaryButton from "../../UI/PrimaryButton";
import LoadingSpinner from "../loadingSpinner/LoadingSpinner";
import { API_BASE_URL } from "../../constants";

const ResetPasswordForm = ({ toggleForm }) => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (password !== confirmPassword) {
      enqueueSnackbar(t("passwordsDoNotMatch"), { variant: "error" });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, token, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMsg =
          data?.errors?.email?.[0] ||
          data?.errors?.token?.[0] ||
          data?.message ||
          t("somethingWentWrong");
        enqueueSnackbar(errorMsg, { variant: "error" });
        return;
      }

      enqueueSnackbar(data.status || data.message || t("passwordResetSuccess"), {
        variant: "success",
      });
      navigate("/");
      dispatch(setFormType("login"));
    } catch {
      enqueueSnackbar(t("somethingWentWrong"), { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-10 z-50"
      onClick={() => toggleForm(null)}
    >
      <div
        className="bg-white p-8 rounded-lg shadow-2xl max-w-md w-full relative m-2"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => toggleForm(null)}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 focus:outline-none"
          aria-label="Close"
        >
          ✕
        </button>
        <h1 className="text-gray-800 text-2xl font-bold text-center mb-6">
          {t("setNewPassword")}
        </h1>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label
              htmlFor="rp-email"
              className="block text-sm font-medium text-gray-600 mb-2"
            >
              {t("email")}
            </label>
            <input
              type="email"
              id="rp-email"
              placeholder={t("enterYourEmail")}
              className="w-full p-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="relative">
            <label
              htmlFor="rp-password"
              className="block text-sm font-medium text-gray-600 mb-2"
            >
              {t("newPassword")}
            </label>
            <input
              type={showPassword ? "text" : "password"}
              id="rp-password"
              placeholder={t("enterYourPassword")}
              className="w-full p-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 top-6 flex items-center pr-3 text-gray-400 hover:text-gray-600 focus:outline-none"
              aria-label={showPassword ? t("hidePassword") : t("showPassword")}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          <div className="relative">
            <label
              htmlFor="rp-confirm-password"
              className="block text-sm font-medium text-gray-600 mb-2"
            >
              {t("confirmPassword")}
            </label>
            <input
              type={showConfirmPassword ? "text" : "password"}
              id="rp-confirm-password"
              placeholder={t("confirmYourPassword")}
              className="w-full p-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-0 top-6 flex items-center pr-3 text-gray-400 hover:text-gray-600 focus:outline-none"
              aria-label={
                showConfirmPassword ? t("hidePassword") : t("showPassword")
              }
            >
              {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          <PrimaryButton
            text={loading ? <LoadingSpinner /> : t("resetPassword")}
            type="submit"
            className={
              loading
                ? "opacity-75 cursor-not-allowed flex justify-center items-center"
                : ""
            }
            disabled={loading}
          />
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordForm;
